import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Table, Tag, Tooltip, Popconfirm, message } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import type { ColumnsType, TableRowSelection } from "antd/es/table/interface";
import { PortfolioPageShell } from "@/views/Portfolio/shared";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import { usePromotedTrials } from "@/hooks/usePromotedTrials";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { SIMPLIFIED_ENTRY_STEP } from "./steps";
import CreatePlanModal from "./components/CreatePlanModal";
import type { TrialPlan } from "./types";

const PHASE_COLORS: Record<string, string> = {
  "Phase 1": "blue",
  "Phase 1/2": "geekblue",
  "Phase 2": "purple",
  "Phase 2/3": "volcano",
  "Phase 3": "orange",
  "Phase 4": "gold",
};

export default function TrialPlanningList() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { plans, createPlan, deletePlan, duplicatePlans } = useTrialPlans();
  const promoteEnabled = useFeatureFlag(FEATURE_FLAGS.PLAN_PROMOTE, true);
  const simplified = useFeatureFlag(FEATURE_FLAGS.PLANNING_SIMPLIFIED, true);
  const noun = simplified ? "scenario" : "plan";
  const addLabel = simplified ? "Add scenario" : "New Plan";
  const { promote, getPromoted } = usePromotedTrials();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Under /portfolio/planning there is no :id param; "portfolio" is the
  // planning shell's catch-all trial id. Open on the first step the flow has —
  // simplified scenarios have no Overview, and linking to it would only bounce.
  const planUrl = (planId: string) =>
    `/planning/${id ?? "portfolio"}/${planId}/${
      simplified ? SIMPLIFIED_ENTRY_STEP : "home"
    }`;

  const handleAddToVivo = () => {
    const selected = plans.filter((p) => selectedKeys.includes(p.id));
    selected.forEach((p) => promote(p));
    message.success(
      `Added ${selected.length} plan${selected.length !== 1 ? "s" : ""} to Vivo`,
    );
    setSelectedKeys([]);
  };

  const handleDuplicate = () => {
    duplicatePlans(selectedKeys);
    message.success(
      `Duplicated ${selectedKeys.length} plan${selectedKeys.length !== 1 ? "s" : ""}`,
    );
    setSelectedKeys([]);
  };

  const handleDelete = () => {
    selectedKeys.forEach((k) => deletePlan(k));
    message.success(
      `Deleted ${selectedKeys.length} plan${selectedKeys.length !== 1 ? "s" : ""}`,
    );
    setSelectedKeys([]);
  };

  const rowSelection: TableRowSelection<TrialPlan> = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => setSelectedKeys(keys as string[]),
    columnWidth: 40,
  };

  const columns: ColumnsType<TrialPlan> = [
    {
      title: "Plan Name",
      dataIndex: "name",
      key: "name",
      render: (name: string) => (
        <span className="font-semibold text-[14px] text-[#1F2933] hover:text-[#0C98D0] cursor-pointer">
          {name}
        </span>
      ),
    },
    {
      title: "Indication",
      dataIndex: "indication",
      key: "indication",
      render: (v: string) => (
        <span className="text-[13px] text-[#6B7280]">{v || "—"}</span>
      ),
    },
    {
      title: "Phase",
      dataIndex: "phase",
      key: "phase",
      render: (v: string) =>
        v ? (
          <Tag color={PHASE_COLORS[v] ?? "default"} className="text-[12px]">
            {v}
          </Tag>
        ) : (
          <span className="text-[#6B7280]">—</span>
        ),
    },
    {
      title: "Sites",
      key: "sites",
      align: "center",
      render: (_, row) => (
        <span className="font-semibold text-[13px] text-[#1F2933]">
          {row.sites.length}
        </span>
      ),
    },
    {
      title: "Milestones with Dates",
      key: "milestones",
      align: "center",
      render: (_, row) => {
        const filled = row.milestones.filter((m) => m.start_date).length;
        const total = row.milestones.length;
        return (
          <span className="text-[13px] text-[#6B7280]">
            {filled}/{total}
          </span>
        );
      },
    },
    ...(promoteEnabled
      ? [
          {
            title: "In Vivo",
            key: "inVivo",
            align: "center" as const,
            render: (_: unknown, row: TrialPlan) => {
              const promoted = getPromoted(row.id);
              if (!promoted)
                return <span className="text-[#D1D5DB] text-[12px]">—</span>;
              const stale =
                promoted.sourceVersion !== undefined &&
                promoted.sourceVersion !== row.updatedAt;
              return stale ? (
                <Tooltip title="The plan has changed since it was published — open it to update the planned trial">
                  <Tag color="gold" className="text-[12px] m-0">
                    Update available
                  </Tag>
                </Tooltip>
              ) : (
                <Tag color="green" className="text-[12px] m-0">
                  In Vivo
                </Tag>
              );
            },
          },
        ]
      : []),
    {
      title: "Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (v: string) => (
        <span className="text-[12px] text-[#6B7280]">
          {new Date(v).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
      ),
    },
  ];

  const n = selectedKeys.length;

  return (
    <PortfolioPageShell
      title={simplified ? "Scenarios" : "Trial Planning"}
      tooltipText={
        simplified
          ? "Build and compare site-selection scenarios for upcoming trials."
          : "Plan and manage site selection and timelines for upcoming trials."
      }
    >
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 min-h-[32px]">
        {n > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-[13px] text-[#6B7280]">
              {n} {noun}
              {n !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={handleDuplicate}
            >
              Duplicate
            </Button>
            {promoteEnabled && (
              <Button
                size="small"
                icon={<CloudUploadOutlined />}
                onClick={handleAddToVivo}
              >
                Add to Vivo
              </Button>
            )}
            <Popconfirm
              title={`Delete ${n} ${noun}${n !== 1 ? "s" : ""}?`}
              description="This cannot be undone."
              okText="Delete"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              onConfirm={handleDelete}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
            <Button
              size="small"
              type="link"
              className="text-[#9CA3AF] px-0"
              onClick={() => setSelectedKeys([])}
            >
              Clear
            </Button>
          </div>
        ) : (
          <p className="text-[13px] text-[#6B7280]">
            {plans.length} {noun}
            {plans.length !== 1 ? "s" : ""}
          </p>
        )}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          className="bg-[#0C98D0] border-none font-semibold shadow-none"
          onClick={() => setModalOpen(true)}
        >
          {addLabel}
        </Button>
      </div>

      {plans.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-[8px] border border-dashed border-[#D1D5DB] bg-white py-16">
          <p className="text-[15px] font-semibold text-[#374151] mb-1">
            {simplified ? "No scenarios yet" : "No trial plans yet"}
          </p>
          <p className="text-[13px] text-[#6B7280] mb-4">
            Create your first {noun} to begin assembling sites
            {simplified ? "" : " and milestones"}.
          </p>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="bg-[#0C98D0] border-none"
            onClick={() => setModalOpen(true)}
          >
            {addLabel}
          </Button>
        </div>
      ) : (
        <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-hidden">
          <Table<TrialPlan>
            dataSource={plans}
            columns={columns}
            rowKey="id"
            pagination={false}
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: () => {
                if (selectedKeys.length > 0) {
                  // In selection mode, clicking a row toggles it
                  setSelectedKeys((prev) =>
                    prev.includes(record.id)
                      ? prev.filter((k) => k !== record.id)
                      : [...prev, record.id],
                  );
                } else {
                  navigate(planUrl(record.id));
                }
              },
              className: "cursor-pointer hover:bg-[#F8FAFC]",
            })}
          />
        </div>
      )}

      <CreatePlanModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreate={(values) => {
          const plan = createPlan(values);
          setModalOpen(false);
          navigate(planUrl(plan.id));
        }}
      />
    </PortfolioPageShell>
  );
}
